import { lazy, Suspense, useEffect } from "react";
import DynamicHero from "@/components/DynamicHero";
import SectionDots from "@/components/SectionDots";
import Seo from "@/components/Seo";

// Below-the-fold sections — lazy-loaded to keep the initial JS task short
// (improves Max Potential FID / TBT). Hero stays eager so LCP isn't delayed.
const retry = <T,>(factory: () => Promise<T>): Promise<T> =>
  factory().catch((err) => {
    const msg = String(err?.message || err);
    if (
      /Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError/i.test(msg) &&
      typeof window !== "undefined"
    ) {
      const KEY = "__chunk_reload__";
      if (!sessionStorage.getItem(KEY)) {
        sessionStorage.setItem(KEY, "1");
        window.location.reload();
        return new Promise<T>(() => {});
      }
    }
    throw err;
  });


const SectionProblem = lazy(() => retry(() => import("@/components/home/SectionProblem")));
const SectionSystemCompact = lazy(() => retry(() => import("@/components/home/SectionSystemCompact")));
const SectionValue = lazy(() => retry(() => import("@/components/home/SectionValue")));
const SectionWhereWeWork = lazy(() => retry(() => import("@/components/home/SectionWhereWeWork")));
const SectionWhatWeDo = lazy(() => retry(() => import("@/components/home/SectionWhatWeDo")));
const SectionLabsCTA = lazy(() => retry(() => import("@/components/home/SectionLabsCTA")));
const SectionVision = lazy(() => retry(() => import("@/components/home/SectionVision")));
const SectionWhereToNext = lazy(() => retry(() => import("@/components/home/SectionWhereToNext")));



const SectionFallback = () => (
  <section className="min-h-[100svh] md:h-screen w-full md:snap-start bg-charcoal" aria-hidden />
);

const sectionMeta = [
  { id: "hero", label: "Intro" },
  { id: "what-we-do", label: "What We Do" },
  { id: "problem", label: "The Problem" },
  { id: "system-teaser", label: "The System" },
  { id: "value", label: "The Value" },
  { id: "projects", label: "Selected Work" },
  { id: "labs", label: "Labs" },
  { id: "vision", label: "Vision" },
  { id: "cta", label: "Contact" },
];


const Index = () => {
  // Smooth scroll to anchor on hash change (used by header nav)
  useEffect(() => {
    const run = () => {
      const hash = window.location.hash;
      if (!hash) return;
      // Defer one frame so lazy sections have mounted.
      requestAnimationFrame(() => {
        import("@/lib/smoothScroll").then((m) => m.scrollToHash(hash));
      });
    };
    run();
    window.addEventListener("hashchange", run);
    return () => window.removeEventListener("hashchange", run);
  }, []);

  return (
    <main
      data-scroll-container
      className="relative overflow-x-hidden scrollbar-hide bg-charcoal md:h-screen md:overflow-y-auto md:snap-y md:snap-mandatory"
    >
      <Seo
        title="codelumen | Architectural Lighting Design Studio"
        description="Architectural lighting, from first concept to final commissioning. codelumen authors the design, engineers the integration, and commissions the installed result."
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "codelumen",
          url: "https://www.clumen.com",
        }}
      />
      <DynamicHero />
      <Suspense fallback={<SectionFallback />}>
        <SectionWhatWeDo />
        <SectionProblem />
        <SectionSystemCompact />
        <SectionValue />
        <SectionWhereWeWork />
        <SectionLabsCTA />
        <SectionVision />
        <SectionWhereToNext />
      </Suspense>



      <SectionDots sections={sectionMeta} />
    </main>
  );
};

export default Index;
